import { useState, useRef } from 'react';
import { uploadFile } from 'zite-file-upload-sdk';
import { saveAssessmentEvidence } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, MessageSquare, Loader2, X, Video, Mic } from 'lucide-react';
import { toast } from 'sonner';
import VoiceRecorder from './VoiceRecorder';

interface EvidenceCaptureProps {
  sessionId: string;
  criterionIndex: number;
  onSaved: () => void;
  onCancel: () => void;
}

export default function EvidenceCapture({ sessionId, criterionIndex, onSaved, onCancel }: EvidenceCaptureProps) {
  const [mode, setMode] = useState<'choose' | 'photo' | 'video' | 'note' | 'voice'>('choose');
  const [uploading, setUploading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [caption, setCaption] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
    setMode('photo');
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Limit to 30 seconds / ~50MB
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB. Keep clips short (under 30 seconds).');
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setMode('video');
  };

  const submitVideo = async () => {
    if (!videoFile) return;
    setUploading(true);
    try {
      const { fileUrl } = await uploadFile({ data: videoFile, filename: `video-evidence-${Date.now()}-${videoFile.name}` });
      await saveAssessmentEvidence({
        sessionId,
        criterionIndex,
        type: 'Video',
        fileUrl,
        caption,
      });
      toast.success('Video evidence saved');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save video');
    } finally {
      setUploading(false);
    }
  };

  const submitPhoto = async () => {
    if (!photoFile) return;
    setUploading(true);
    try {
      const { fileUrl } = await uploadFile({ data: photoFile, filename: `evidence-${Date.now()}-${photoFile.name}` });
      await saveAssessmentEvidence({
        sessionId,
        criterionIndex,
        type: 'Photo',
        fileUrl,
        caption,
      });
      toast.success('Photo evidence saved');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save evidence');
    } finally {
      setUploading(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setUploading(true);
    try {
      await saveAssessmentEvidence({
        sessionId,
        criterionIndex,
        type: 'Note',
        transcriptText: noteText.trim(),
      });
      toast.success('Note saved');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save note');
    } finally {
      setUploading(false);
    }
  };

  if (mode === 'choose') {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1 p-3 bg-background border border-border rounded-xl text-xs font-medium text-foreground touch-manipulation active:scale-[0.98] transition-transform"
          >
            <Camera className="w-4 h-4" /> Photo
          </button>
          <button
            onClick={() => videoRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1 p-3 bg-background border border-border rounded-xl text-xs font-medium text-foreground touch-manipulation active:scale-[0.98] transition-transform"
          >
            <Video className="w-4 h-4" /> Video
          </button>
          <button
            onClick={() => setMode('note')}
            className="flex flex-col items-center justify-center gap-1 p-3 bg-background border border-border rounded-xl text-xs font-medium text-foreground touch-manipulation active:scale-[0.98] transition-transform"
          >
            <MessageSquare className="w-4 h-4" /> Note
          </button>
        </div>
        <button
          onClick={() => setMode('voice')}
          className="flex items-center justify-center gap-1.5 w-full p-2.5 bg-background border border-border rounded-xl text-xs font-medium text-foreground touch-manipulation active:scale-[0.98] transition-transform"
        >
          <Mic className="w-4 h-4" /> Voice Note (with transcription)
        </button>
        <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoSelect} />
        <input ref={videoRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={handleVideoSelect} />
      </div>
    );
  }

  if (mode === 'video') {
    return (
      <div className="space-y-2">
        {videoPreview && (
          <div className="relative">
            <video src={videoPreview} controls className="w-full h-32 rounded-lg object-cover bg-black" />
            <button onClick={() => { setVideoPreview(''); setVideoFile(null); setMode('choose'); }}
              className="absolute top-1.5 right-1.5 w-6 h-6 bg-background/80 rounded-full flex items-center justify-center">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <Textarea
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Add a caption (optional)..."
          className="text-sm h-16 resize-none"
        />
        <div className="flex gap-2">
          <Button onClick={submitVideo} disabled={uploading || !videoFile} className="flex-1 h-10 text-sm touch-manipulation">
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Uploading...</> : 'Save Video'}
          </Button>
          <Button variant="outline" onClick={() => { setVideoPreview(''); setVideoFile(null); setMode('choose'); }} className="h-10 text-sm touch-manipulation">
            Cancel
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">Keep clips under 30 seconds (max 50MB)</p>
      </div>
    );
  }

  if (mode === 'photo') {
    return (
      <div className="space-y-2">
        {photoPreview && (
          <div className="relative">
            <img src={photoPreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
            <button onClick={() => { setPhotoPreview(''); setPhotoFile(null); setMode('choose'); }}
              className="absolute top-1.5 right-1.5 w-6 h-6 bg-background/80 rounded-full flex items-center justify-center">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <Textarea
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Add a caption (optional)..."
          className="text-sm h-16 resize-none"
        />
        <div className="flex gap-2">
          <Button onClick={submitPhoto} disabled={uploading || !photoFile} className="flex-1 h-10 text-sm touch-manipulation">
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Uploading...</> : 'Save Photo'}
          </Button>
          <Button variant="outline" onClick={() => { setPhotoPreview(''); setPhotoFile(null); setMode('choose'); }} className="h-10 text-sm touch-manipulation">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Voice mode
  if (mode === 'voice') {
    return (
      <VoiceRecorder
        sessionId={sessionId}
        criterionIndex={criterionIndex}
        onSaved={onSaved}
        onCancel={() => setMode('choose')}
      />
    );
  }

  // Note mode
  return (
    <div className="space-y-2">
      <Textarea
        value={noteText}
        onChange={e => setNoteText(e.target.value)}
        placeholder="Type your observation..."
        className="text-sm h-20 resize-none"
        autoFocus
      />
      <div className="flex gap-2">
        <Button onClick={submitNote} disabled={uploading || !noteText.trim()} className="flex-1 h-10 text-sm touch-manipulation">
          {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Saving...</> : 'Save Note'}
        </Button>
        <Button variant="outline" onClick={() => { setNoteText(''); setMode('choose'); }} className="h-10 text-sm touch-manipulation">
          Cancel
        </Button>
      </div>
    </div>
  );
}
